<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { logout } from '@accor/customer-lib'
import type { AccorCurrencies } from '@accor/core-services/src/pos'
import { storeToRefs } from 'pinia'
import { i18nService } from '@accor/customer-vue-services'
import { getHeaderItems, type HeaderItemsResponse } from '@/services/aem'
import { useWebAppStore } from '@/stores/webApp'
import { useUserStore } from '@/stores/user'
import { useEmployeeStore } from '@/stores/employee'
import { useCompanyStore } from '@/stores/company'
import { ACCOUNT_B2C_CLIENT } from '@/constants/urls'
import { useRoute, useRouter } from 'vue-router'
import {
  getSelectedGeolocation,
  getAccorGeolocations,
  updateGeolocation
} from '@/services/geolocationService'
import { getSelectedCurrency, getAccorCurrencies, updateCurrency } from '@/services/currencyService'
import type { SelectedActionData, SelectedCurrency } from '@/services/currencyService'
import type { SelectedGeolocation, AccorGeoLocations } from '@/services/geolocationService'
import AhHeader from '@accor/ah-navigation/src/components/AhHeader/AhHeader.vue'
import LoginNav from '@accor/login-nav/src/components/LoginNav.vue'
import RelatedDropdown from '@accor/related-dropdown/src/components/RelatedDropdown.vue'
import HeaderLogo from '@/assets/icons/brands/all.svg'

const router = useRouter()
const route = useRoute()

const webAppStore = useWebAppStore()
const userStore = useUserStore()
const employeeStore = useEmployeeStore()
const companyStore = useCompanyStore()

const {
  username,
  rewardsPoints,
  last12MonthRewardsPoints,
  productTier,
  balances,
  loyaltyCard,
  hasWallet
} = storeToRefs(userStore)

const { isAuthorizedForAab } = storeToRefs(employeeStore)
const { company } = storeToRefs(companyStore)

const listItems = ref<HeaderItemsResponse>([])
const locale = computed(() => i18nService.getCurrentLocale())
const selectedGeolocation = ref<SelectedGeolocation>({})
const accorGeolocations = ref<AccorGeoLocations>({})
const selectedCurrency = ref<SelectedCurrency>({})
const accorCurrencies = ref<AccorCurrencies>({})

onMounted(async () => {
  selectedGeolocation.value = await getSelectedGeolocation()
  accorGeolocations.value = await getAccorGeolocations()

  selectedCurrency.value = await getSelectedCurrency()
  accorCurrencies.value = await getAccorCurrencies()

  listItems.value = await getHeaderItems(locale.value)
})

const actionCallbacks = {
  disconnectAction: async () => {
    await logout()
    window.location.href = '/'
  }
}

const updateGeolocationAndRedirect = (selectedData: SelectedActionData) =>
  updateGeolocation(selectedData, route, router)

const userInfo = computed(() => {
  if (!webAppStore.isUserInfoReady) {
    return null
  }

  return {
    username: username.value,
    points: rewardsPoints.value,
    statusPoints: last12MonthRewardsPoints.value,
    nights: balances.value.currentNightsBalance || 0,
    statut: productTier.value,
    currentNightsBalance: balances.value.currentNightsBalance || 0,
    endDate: loyaltyCard.value?.endDate || null,
    hasWallet: hasWallet.value,
    enterpriseName: isAuthorizedForAab.value
      ? company.value?.displayName || company.value?.name
      : undefined
  }
})
</script>

<template>
  <AhHeader
    v-if="listItems.length"
    :list-items="listItems"
    :lang="locale"
    :geoloc="selectedGeolocation?.geolocationItem"
    :currency="selectedCurrency.currencyItem"
    is-sticky
  >
    <template #logo>
      <a href="/">
        <img loading="lazy" :src="HeaderLogo" alt="Accor Live Limitless" />
      </a>
    </template>
    <template #loginnav>
      <LoginNav
        :lang="locale"
        :is-loading="!webAppStore.isUserInfoReady"
        :is-connected="webAppStore.isUserInfoReady"
        :is-member="!!loyaltyCard"
        :status-info="userInfo"
        :action-callbacks="actionCallbacks"
        :enterprise-client="ACCOUNT_B2C_CLIENT"
      />
    </template>
    <template v-if="accorGeolocations.geolocOptionsData" #geoloc>
      <RelatedDropdown
        :sdd-options="accorGeolocations.geolocOptionsData"
        :first-sdd-selected="selectedGeolocation.geolocationDropdown.continent"
        :second-sdd-selected="selectedGeolocation.geolocationDropdown.selectedOption"
        :lang="locale"
        @get-relateddropdown-data="updateGeolocationAndRedirect"
      />
    </template>
    <template v-if="accorCurrencies.currencies" #currency>
      <RelatedDropdown
        trad-context-pre="currencySelector"
        :sdd-options="accorCurrencies.currencies"
        :first-sdd-selected="selectedCurrency.currencyDropdown.continent"
        :second-sdd-selected="selectedCurrency.currencyDropdown.currency"
        :lang="locale"
        @get-relateddropdown-data="updateCurrency"
      />
    </template>
  </AhHeader>
</template>
